"use client";

import { Grid3X3, Home, ShoppingCart } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";

import { homeContent } from "@/data/home-content";
import { subtleTween } from "@/components/ui/motion-primitives";

export type BottomNavigationActiveItem = "home" | "categories" | "location" | "cart";

export interface BottomNavigationProps {
  readonly activeItem: BottomNavigationActiveItem;
  readonly cartCount: number;
}

const items = [
  { key: "home", href: "/", label: homeContent.bottomNavItems[0], renderIcon: renderHomeIcon },
  {
    key: "categories",
    href: "/categorias",
    label: homeContent.bottomNavItems[1],
    renderIcon: renderCategoriesIcon,
  },
  {
    key: "location",
    href: "/ubicacion",
    label: homeContent.bottomNavItems[2],
    renderIcon: renderLocationIcon,
  },
  { key: "cart", href: "/pedido", label: homeContent.bottomNavItems[3], renderIcon: renderCartIcon },
] as const;

export function BottomNavigation({
  activeItem,
  cartCount,
}: Readonly<BottomNavigationProps>) {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-outline-variant bg-surface pb-[env(safe-area-inset-bottom)] shadow-sm md:hidden">
      <div className="mx-auto grid h-14 max-w-md grid-cols-4">
        {items.map(({ key, href, label, renderIcon }) => {
          const isActive = activeItem === key;

          return (
            <Link
              className={`relative flex flex-col items-center justify-center rounded-sm outline-none focus-visible:bg-primary/5 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
                isActive
                  ? "text-primary"
                  : "text-muted"
              }`}
              data-bottom-nav-item
              href={href}
              key={key}
              prefetch={false}
            >
              {isActive ? (
                <motion.span
                  aria-hidden="true"
                  className="!absolute left-0 right-0 top-0 h-0.5 bg-primary"
                  layoutId="bottom-navigation-active-indicator"
                  transition={subtleTween}
                />
              ) : null}
              <span className="relative">
                {renderIcon(isActive)}
                <AnimatePresence>
                  {key === "cart" && cartCount > 0 ? (
                    <motion.span
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-secondary px-1 text-[10px] font-bold text-white"
                      exit={{ opacity: 0, scale: 0.9 }}
                      initial={{ opacity: 0, scale: 0.9 }}
                      key="bottom-cart-count"
                      transition={subtleTween}
                    >
                      {cartCount}
                    </motion.span>
                  ) : null}
                </AnimatePresence>
              </span>
              <span className="mt-0.5 text-[10px] font-extrabold tracking-wide">{label}</span>
            </Link>
          );
        })}
      </div>
      <div className="brand-stripe h-1 w-full" />
    </nav>
  );
}

function renderHomeIcon(): ReactNode {
  return <Home aria-hidden="true" className="opacity-95" size={18} strokeWidth={2.2} />;
}

function renderCategoriesIcon(): ReactNode {
  return <Grid3X3 aria-hidden="true" className="opacity-95" size={18} strokeWidth={2.2} />;
}

function renderLocationIcon(isActive: boolean): ReactNode {
  return (
    <Image
      alt=""
      aria-hidden="true"
      className={isActive ? "" : "opacity-80"}
      height={18}
      src="/icons/map-location-pin-svgrepo-com.svg"
      width={18}
    />
  );
}

function renderCartIcon(): ReactNode {
  return <ShoppingCart aria-hidden="true" className="opacity-95" size={20} strokeWidth={2.2} />;
}
